import { Option } from '@code/innovance-react-ui-kit';

import {
  CardDeliveryComboType,
  CardUsageType,
  CustomerTaxInfoFormatResponse,
  DocumentPageValidationComponentsResponse,
  ScoringJobPageVisibility,
  ThirdPartyPageValidationComponentsResponse,
} from 'src/front/api/generated';

import {
  ACCEPTED_FILE_UPLOAD_FORMATS_ARRAY,
  MAX_FILE_UPLOAD_LIMIT_IN_BYTES,
} from 'src/front/common/constants/files';
import { IS_PROVIDED, MINIMUM_DATE_CONTROL, YES_NO } from 'src/front/constant';

import { PhoneValidations } from 'src/front/pages/applications/card-entry/phone-email-entry/PhoneEmailEntry';
import * as Yup from 'yup';
import { containsLatinFIO, formatBlank } from './formUtils';

import { addDays, extractDays } from './validationUtils';

// TODO (emre.yildirim): move these schmeas to common/validations/cardEntry.ts

export const minimumLengthTest = (minimumLength: number, message: string): Yup.TestOptions => ({
  name: 'minimum-length',
  message,
  test: (val: string): boolean => val.length >= (minimumLength || 0),
});

const expireDateValidation = (
  formValidations: DocumentPageValidationComponentsResponse,
  todayMinControl: Date,
  schema: Yup.DateSchema
): any => {
  if (!formValidations?.expireDate?.enabled) {
    return schema;
  }
  schema.min(todayMinControl, 'general_PastDateCanNotSelected_errorMessage');
  if (formValidations?.expireDate?.required) {
    return schema.required('general_requiredField_errorMessage');
  }
  return schema.notRequired();
};

export const LoginSchema = Yup.object().shape({
  username: Yup.string()
    .required('general_requiredField_errorMessage')
    .test(minimumLengthTest(5, 'login_login_shortUsername_errorMessage')),
  password: Yup.string()
    .required('general_requiredField_errorMessage')
    .test(minimumLengthTest(8, 'login_login_shortPassword_errorMessage')),
});

export const ProductOfferSchema = Yup.object().shape({
  customerType: Yup.object().nullable().required('general_requiredField_errorMessage'),
  customerSubType: Yup.object().nullable().required('general_requiredField_errorMessage'),
  debitFlag: Yup.object().nullable().required('general_requiredField_errorMessage'),
  resident: Yup.object().nullable().required('general_requiredField_errorMessage'),
  accountType: Yup.object().nullable().required('general_requiredField_errorMessage'),
  brandOwner: Yup.object().nullable().required('general_requiredField_errorMessage'),
  productCardType: Yup.object().nullable().required('general_requiredField_errorMessage'),
});

export const ProductInformationSchema = Yup.object().shape({
  pos: Yup.object().nullable().required('general_requiredField_errorMessage'),
  subPos: Yup.object().nullable().required('general_requiredField_errorMessage'),
  cardType: Yup.object().nullable().required('general_requiredField_errorMessage'),
  printType: Yup.object().nullable().required('general_requiredField_errorMessage'),
  cardSelectionType: Yup.string(),
  transferableCardInfo: Yup.object().when(
    'isProductTransfer',
    (isProductTransfer: boolean, schema: Yup.ObjectSchema) =>
      isProductTransfer
        ? schema.nullable().required('general_requiredField_errorMessage')
        : schema.nullable().notRequired()
  ),
  mainCard: Yup.object().when('cardUsageType', (cardUsageType: string, schema: Yup.ObjectSchema) =>
    cardUsageType === CardUsageType.PRIMARY
      ? schema.nullable().notRequired()
      : schema.nullable().required('general_requiredField_errorMessage')
  ),
  card: Yup.object().when('isProductOffer', (isProductOffer: boolean, schema: Yup.ObjectSchema) =>
    isProductOffer
      ? schema.nullable().notRequired()
      : schema.nullable().required('general_requiredField_errorMessage')
  ),
  customerType: Yup.object().when(
    'isProductOffer',
    (isProductOffer: boolean, schema: Yup.ObjectSchema) =>
      isProductOffer
        ? schema.nullable().notRequired()
        : schema.nullable().required('general_requiredField_errorMessage')
  ),
  customerSubType: Yup.object().when(
    'isProductOffer',
    (isProductOffer: boolean, schema: Yup.ObjectSchema) =>
      isProductOffer
        ? schema.nullable().notRequired()
        : schema.nullable().required('general_requiredField_errorMessage')
  ),
  debitFlag: Yup.object().when(
    'isProductOffer',
    (isProductOffer: boolean, schema: Yup.ObjectSchema) =>
      isProductOffer
        ? schema.nullable().notRequired()
        : schema.nullable().required('general_requiredField_errorMessage')
  ),
  resident: Yup.object().when(
    'isProductOffer',
    (isProductOffer: boolean, schema: Yup.ObjectSchema) =>
      isProductOffer
        ? schema.nullable().notRequired()
        : schema.nullable().required('general_requiredField_errorMessage')
  ),
  accountType: Yup.object().when(
    'isProductOffer',
    (isProductOffer: boolean, schema: Yup.ObjectSchema) =>
      isProductOffer
        ? schema.nullable().notRequired()
        : schema.nullable().required('general_requiredField_errorMessage')
  ),
  brandOwner: Yup.object().when(
    'isProductOffer',
    (isProductOffer: boolean, schema: Yup.ObjectSchema) =>
      isProductOffer
        ? schema.nullable().notRequired()
        : schema.nullable().required('general_requiredField_errorMessage')
  ),
  productCardType: Yup.object().when(
    'isProductOffer',
    (isProductOffer: boolean, schema: Yup.ObjectSchema) =>
      isProductOffer
        ? schema.nullable().notRequired()
        : schema.nullable().required('general_requiredField_errorMessage')
  ),
});

export const CustomerSearchSchema = Yup.object().shape(
  {
    customerName: Yup.string().required('general_requiredField_errorMessage'),
    customerSurname: Yup.string().required('general_requiredField_errorMessage'),
    customerFatherName: Yup.string(),
    birthDate: Yup.date().when(
      '$todayMaxControl',
      (todayMaxControl: Date, schema: Yup.DateSchema) =>
        schema
          .nullable()
          .min(MINIMUM_DATE_CONTROL, 'cardEntry_minimumYear_errorMessage')
          .max(todayMaxControl, 'general_FutureDateCanNotSelected_errorMessage')
          .required('general_requiredField_errorMessage')
    ),
    identityDocumentSerialCode: Yup.string().notRequired(),
    identityDocumentSerialNumber: Yup.string().when(
      'identityDocumentSerialCode',
      (identityDocumentSerialCode: string, schema: Yup.StringSchema) =>
        identityDocumentSerialCode
          ? schema.required('general_requiredField_errorMessage')
          : schema.notRequired()
    ),
  },
  [['identityDocumentSerialCode', 'identityDocumentSerialNumber']]
);

export const IndividualInformationSchema = Yup.object().shape({
  customerName: Yup.string().required('general_requiredField_errorMessage'),
  customerSurname: Yup.string().required('general_requiredField_errorMessage'),
  customerGender: Yup.object().nullable().required('general_requiredField_errorMessage'),
  birthDate: Yup.date().when('$todayMaxControl', (todayMaxControl: Date, schema: Yup.DateSchema) =>
    schema
      .nullable()
      .min(MINIMUM_DATE_CONTROL, 'cardEntry_minimumYear_errorMessage')
      .max(todayMaxControl, 'general_FutureDateCanNotSelected_errorMessage')
      .required('general_requiredField_errorMessage')
  ),
  birthPlaceCountry: Yup.object().nullable().required('general_requiredField_errorMessage'),
  citizenship: Yup.object().nullable().required('general_requiredField_errorMessage'),
  birthPlaceRegion: Yup.object()
    .nullable()
    .when('birthPlaceCountry', (birthPlaceCountry: Option, schema: Yup.ObjectSchema) =>
      birthPlaceCountry?.value === 'RU'
        ? schema.required('general_requiredField_errorMessage')
        : schema.notRequired()
    ),
  residenceCountry: Yup.object().nullable().required('general_requiredField_errorMessage'),
  birthPlaceCity: Yup.string().required('general_requiredField_errorMessage'),
  hasPreviousSurname: Yup.object().when(
    'newCustomer',
    (newCustomer: Yup.BooleanSchema, schema: Yup.ObjectSchema) =>
      newCustomer
        ? schema.nullable().required('general_requiredField_errorMessage')
        : schema.notRequired()
  ),
  previousSurname: Yup.string().when(
    'hasPreviousSurname',
    (hasPreviousSurname: Option, schema: Yup.StringSchema) =>
      hasPreviousSurname?.value === YES_NO.YES
        ? schema.required('general_requiredField_errorMessage')
        : schema.notRequired()
  ),
  newCustomer: Yup.bool(),
  customerSurnameNonresident: Yup.string()
    .nullable()
    .when(
      ['customerSurname', 'residenceCountry'],
      (customerSurname: string, residenceCountry: { value: string }, schema: Yup.ObjectSchema) =>
        customerSurname !== '' &&
        (residenceCountry.value !== 'RU' || containsLatinFIO(customerSurname))
          ? schema.required('general_requiredField_errorMessage')
          : schema.notRequired()
    ),
  customerNameNonresident: Yup.string()
    .nullable()
    .when(
      ['customerName', 'residenceCountry'],
      (customerName: string, residenceCountry: { value: string }, schema: Yup.ObjectSchema) =>
        customerName !== '' && (residenceCountry.value !== 'RU' || containsLatinFIO(customerName))
          ? schema.required('general_requiredField_errorMessage')
          : schema.notRequired()
    ),
  customerSecondNameNonresident: Yup.string()
    .nullable()
    .when(
      ['customerSecondName', 'residenceCountry'],
      (customerSecondName: string, residenceCountry: { value: string }, schema: Yup.ObjectSchema) =>
        customerSecondName !== '' &&
        (residenceCountry.value !== 'RU' || containsLatinFIO(customerSecondName))
          ? schema.required('general_requiredField_errorMessage')
          : schema.notRequired()
    ),
});

export const IdentitySchema = Yup.object().shape({
  identityType: Yup.object().nullable().required('general_requiredField_errorMessage'),
  identityDocumentSerialCode: Yup.string().when(
    '$formValidations',
    (formValidations: DocumentPageValidationComponentsResponse, schema: Yup.StringSchema) =>
      formValidations?.series.required
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                formValidations?.series.minimumLength || 0,
                'general_minCharacter_errorMessage'
              )
            )
        : schema
            .notRequired()
            .test(
              'fixed-length',
              'general_minCharacter_errorMessage',
              (val) => val === '' || val.length >= (formValidations?.series.minimumLength || 0)
            )
  ),
  identityDocumentSerialNumber: Yup.string().when(
    '$formValidations',
    (formValidations: DocumentPageValidationComponentsResponse, schema: Yup.StringSchema) =>
      formValidations?.documentNumber.required
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                formValidations?.documentNumber.minimumLength || 0,
                'general_minCharacter_errorMessage'
              )
            )
        : schema
            .notRequired()
            .test(
              'fixed-length',
              'general_minCharacter_errorMessage',
              (val) =>
                val === '' || val.length >= (formValidations?.documentNumber.minimumLength || 0)
            )
  ),
  issueDate: Yup.date()
    .nullable()
    .when(
      ['$formValidations', '$todayMaxControl'],
      (
        formValidations: DocumentPageValidationComponentsResponse,
        todayMaxControl: Date,
        schema: Yup.DateSchema
      ) =>
        formValidations?.issueDate?.required
          ? schema
              .min(MINIMUM_DATE_CONTROL, 'cardEntry_minimumYear_errorMessage')
              .max(todayMaxControl, 'general_FutureDateCanNotSelected_errorMessage')
              .required('general_requiredField_errorMessage')
          : schema.notRequired()
    ),
  expireDate: Yup.date()
    .nullable()
    .when(
      ['$formValidations', '$todayMinControl'],
      (
        formValidations: DocumentPageValidationComponentsResponse,
        todayMinControl: Date,
        schema: Yup.DateSchema
      ) => expireDateValidation(formValidations, todayMinControl, schema)
    ),
  issuedBy: Yup.string().when(
    '$formValidations',
    (formValidations: DocumentPageValidationComponentsResponse, schema: Yup.StringSchema) =>
      formValidations?.issuedBy.required
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                formValidations?.issuedBy.minimumLength || 0,
                'general_minCharacter_errorMessage'
              )
            )
        : schema
            .test(
              'fixed-length',
              'general_minCharacter_errorMessage',
              (val) => val === '' || val.length >= (formValidations?.issuedBy.minimumLength || 0)
            )
            .notRequired()
  ),
  issueCountry: Yup.object()
    .nullable()
    .when(
      '$formValidations',
      (formValidations: DocumentPageValidationComponentsResponse, schema: Yup.StringSchema) =>
        formValidations?.country?.required
          ? schema.required('general_requiredField_errorMessage')
          : schema.notRequired()
    ),
  unitCode: Yup.string().when(
    '$formValidations',
    (formValidations: DocumentPageValidationComponentsResponse, schema: Yup.StringSchema) =>
      formValidations?.unitCode.required
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                formValidations?.unitCode.minimumLength || 0,
                'general_minCharacter_errorMessage'
              )
            )
        : schema
            .test(
              'fixed-length',
              'general_minCharacter_errorMessage',
              (val) => val === '' || val.length >= (formValidations?.unitCode.minimumLength || 0)
            )
            .notRequired()
  ),
});
export const LoanIdentitySchema = Yup.object().shape({
  identityType: Yup.object().nullable().required('general_requiredField_errorMessage'),
  identityDocumentSerialCode: Yup.string().when(
    '$formValidations',
    (formValidations: DocumentPageValidationComponentsResponse, schema: Yup.StringSchema) =>
      formValidations?.series.required
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                formValidations?.series.minimumLength || 0,
                'general_minCharacter_errorMessage'
              )
            )
        : schema
            .notRequired()
            .test(
              'fixed-length',
              'general_minCharacter_errorMessage',
              (val) => val === '' || val.length >= (formValidations?.series.minimumLength || 0)
            )
  ),
  identityDocumentSerialNumber: Yup.string().when(
    '$formValidations',
    (formValidations: DocumentPageValidationComponentsResponse, schema: Yup.StringSchema) =>
      formValidations?.documentNumber.required
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                formValidations?.documentNumber.minimumLength || 0,
                'general_minCharacter_errorMessage'
              )
            )
        : schema
            .notRequired()
            .test(
              'fixed-length',
              'general_minCharacter_errorMessage',
              (val) =>
                val === '' || val.length >= (formValidations?.documentNumber.minimumLength || 0)
            )
  ),
  issueDate: Yup.date()
    .nullable()
    .when(
      ['$formValidations', '$todayMaxControl'],
      (
        formValidations: DocumentPageValidationComponentsResponse,
        todayMaxControl: Date,
        schema: Yup.DateSchema
      ) =>
        formValidations?.issueDate?.required
          ? schema
              .min(MINIMUM_DATE_CONTROL, 'cardEntry_minimumYear_errorMessage')
              .max(todayMaxControl, 'general_FutureDateCanNotSelected_errorMessage')
              .required('general_requiredField_errorMessage')
          : schema.notRequired()
    ),
  expireDate: Yup.date()
    .nullable()
    .when(
      ['$formValidations', '$todayMinControl'],
      (
        formValidations: DocumentPageValidationComponentsResponse,
        todayMinControl: Date,
        schema: Yup.DateSchema
      ) => expireDateValidation(formValidations, todayMinControl, schema)
    ),
  issuedBy: Yup.string().when(
    '$formValidations',
    (formValidations: DocumentPageValidationComponentsResponse, schema: Yup.StringSchema) =>
      formValidations?.issuedBy.required
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                formValidations?.issuedBy.minimumLength || 0,
                'general_minCharacter_errorMessage'
              )
            )
        : schema
            .test(
              'fixed-length',
              'general_minCharacter_errorMessage',
              (val) => val === '' || val.length >= (formValidations?.issuedBy.minimumLength || 0)
            )
            .notRequired()
  ),

  unitCode: Yup.string().when(
    '$formValidations',
    (formValidations: DocumentPageValidationComponentsResponse, schema: Yup.StringSchema) =>
      formValidations?.unitCode.required
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                formValidations?.unitCode.minimumLength || 0,
                'general_minCharacter_errorMessage'
              )
            )
        : schema
            .test(
              'fixed-length',
              'general_minCharacter_errorMessage',
              (val) => val === '' || val.length >= (formValidations?.unitCode.minimumLength || 0)
            )
            .notRequired()
  ),
});
export const DocumentUploadSchema = Yup.object().shape({
  files: Yup.mixed().when('documentId', (documentId: Yup.StringSchema, schema: Yup.MixedSchema) =>
    !documentId
      ? schema
          .required('general_requiredField_errorMessage')
          .test(
            'file-size',
            'general_uploadFileSize_errorMessage',
            (files) => files && files[0] && files[0].size < MAX_FILE_UPLOAD_LIMIT_IN_BYTES
          )
          .test(
            'file-format',
            'general_uploadFileFormat_errorMessage',
            (files) =>
              files && files[0] && ACCEPTED_FILE_UPLOAD_FORMATS_ARRAY.includes(files[0].type)
          )
      : schema.notRequired()
  ),
  filePath: Yup.string(),
  documentType: Yup.object().nullable().required('general_requiredField_errorMessage'),
  explanation: Yup.string(),
  documentId: Yup.string(),
});

export const SimpleDocumentUploadSchema = Yup.object().shape({
  files: Yup.mixed()
    .required('general_requiredField_errorMessage')
    .test(
      'file-size',
      'general_uploadFileSize_errorMessage',
      (files) => files && files[0] && files[0].size < MAX_FILE_UPLOAD_LIMIT_IN_BYTES
    )
    .test(
      'file-format',
      'general_uploadFileFormat_errorMessage',
      (files) => files && files[0] && ACCEPTED_FILE_UPLOAD_FORMATS_ARRAY.includes(files[0].type)
    ),
  filePath: Yup.string(),
  explanation: Yup.string(),
});

export const DocumentUploadSchemaWithoutDocumentType = Yup.object().shape({
  files: Yup.mixed().when('documentId', (documentId: Yup.StringSchema, schema: Yup.MixedSchema) =>
    !documentId
      ? schema
          .required('general_requiredField_errorMessage')
          .test(
            'file-size',
            'general_uploadFileSize_errorMessage',
            (files) => files && files[0] && files[0].size < MAX_FILE_UPLOAD_LIMIT_IN_BYTES
          )
          .test(
            'file-format',
            'general_uploadFileFormat_errorMessage',
            (files) =>
              files && files[0] && ACCEPTED_FILE_UPLOAD_FORMATS_ARRAY.includes(files[0].type)
          )
      : schema.notRequired()
  ),
  filePath: Yup.string(),
  explanation: Yup.string(),
});

export const AddressSchema = Yup.object().shape({
  addressType: Yup.object().nullable().required('general_requiredField_errorMessage'),
  isRegistration: Yup.bool(),
  addressQuery: Yup.string().when(
    ['suggestion', 'manualAddress'],
    (suggestion: Yup.ObjectSchema, manualAddress: Yup.BooleanSchema, schema: Yup.StringSchema) =>
      schema.required('general_requiredField_errorMessage').test(
        'no-address',
        // TODO(ozgunbal): missing localization
        'Select and adress or continue with manual entry',
        () => !!suggestion || manualAddress
      )
  ),
  registrationDate: Yup.date().when(
    ['$isCreditCard', 'addressType', '$todayMaxControl'],
    (isCreditCard: boolean, addressType: Option, todayMaxControl: Date, schema: Yup.DateSchema) =>
      addressType?.value === 'RA' || (addressType?.value === 'AL' && isCreditCard)
        ? schema
            .nullable()
            .min(MINIMUM_DATE_CONTROL, 'cardEntry_minimumYear_errorMessage')
            .max(todayMaxControl, 'general_FutureDateCanNotSelected_errorMessage')
            .required('general_requiredField_errorMessage')
        : schema.nullable().notRequired()
  ),
  country: Yup.object().nullable().required('general_requiredField_errorMessage'),
  region: Yup.object().nullable().required('general_requiredField_errorMessage'),
  city: Yup.string().required('general_requiredField_errorMessage'),
  district: Yup.object().nullable().notRequired(),
  streetType: Yup.object().nullable().required('general_requiredField_errorMessage'),
  street: Yup.string().when('addressType', (addressType: Option, schema: Yup.StringSchema) =>
    addressType?.value === 'WA'
      ? schema.required('general_requiredField_errorMessage')
      : schema.notRequired()
  ),
  houseNumber: Yup.string().when('addressType', (addressType: Option, schema: Yup.StringSchema) =>
    addressType?.value === 'WA'
      ? schema.required('general_requiredField_errorMessage')
      : schema.notRequired()
  ),
  housingStructure: Yup.string(),
  flat: Yup.string(),
  zipCode: Yup.string(),
});

export const MobilePhoneSchema = Yup.object().shape({
  mobilePhoneCountry: Yup.object().nullable().required('general_requiredField_errorMessage'),
  mobilePhoneFieldCode: Yup.string().when(
    '$formValidations',
    (formValidations: PhoneValidations, schema: Yup.StringSchema) =>
      schema
        .required('general_requiredField_errorMessage')
        .test(
          minimumLengthTest(
            formValidations?.mobilePhone?.minFieldCodeLength || 0,
            'general_minCharacter_errorMessage'
          )
        )
  ),
  mobilePhoneTelephoneNo: Yup.string().when(
    '$formValidations',
    (formValidations: PhoneValidations, schema: Yup.StringSchema) =>
      schema
        .required('general_requiredField_errorMessage')
        .test(
          minimumLengthTest(
            formValidations?.mobilePhone?.minTelephoneNoLength || 0,
            'general_minCharacter_errorMessage'
          )
        )
  ),
});
export const PhoneEmailSchema = Yup.object().shape({
  emailAddress: Yup.string()
    // .required('general_requiredField_errorMessage')
    .nullable()
    .transform((curr, orig) => (orig === '' ? null : curr))
    .matches(
      // eslint-disable-next-line max-len
      // /^(([^<>()[\]\\.,;:\s@"öüışçğ]+(\.[^<>()[\]\\.,;:\s@"öüışçğ]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/,
      'cardEntry_phoneEmailEntry_invalidEmailFormat_errorMessage'
    ),
  mobilePhoneCountry: Yup.object().nullable().required('general_requiredField_errorMessage'),
  mobilePhoneFieldCode: Yup.string().when(
    '$formValidations',
    (formValidations: PhoneValidations, schema: Yup.StringSchema) =>
      schema
        .required('general_requiredField_errorMessage')
        .test(
          minimumLengthTest(
            formValidations?.mobilePhone?.minFieldCodeLength || 0,
            'general_minCharacter_errorMessage'
          )
        )
  ),
  mobilePhoneTelephoneNo: Yup.string().when(
    '$formValidations',
    (formValidations: PhoneValidations, schema: Yup.StringSchema) =>
      schema
        .required('general_requiredField_errorMessage')
        .test(
          minimumLengthTest(
            formValidations?.mobilePhone?.minTelephoneNoLength || 0,
            'general_minCharacter_errorMessage'
          )
        )
  ),
  actualLivingAddressPhoneCountry: Yup.object().nullable(),
  actualLivingAddressPhoneFieldCode: Yup.string().when(
    '$formValidations',
    (formValidations: PhoneValidations, schema: Yup.StringSchema) =>
      schema.test(
        'fixed-length',
        'general_minCharacter_errorMessage',
        (val) =>
          val === '' ||
          val.length >= (formValidations?.actualLivingAddressPhone?.minFieldCodeLength || 0)
      )
  ),
  actualLivingAddressPhoneTelephoneNo: Yup.string().when(
    '$formValidations',
    (formValidations: PhoneValidations, schema: Yup.StringSchema) =>
      schema.test(
        'fixed-length',
        'general_minCharacter_errorMessage',
        (val) =>
          val === '' ||
          val.length >= (formValidations?.actualLivingAddressPhone?.minTelephoneNoLength || 0)
      )
  ),
  workPhoneCountry: Yup.object()
    .nullable()
    .when('$isCreditCard', (isCreditCard: boolean, schema: Yup.StringSchema) =>
      isCreditCard ? schema.required('general_requiredField_errorMessage') : schema.notRequired()
    ),
  workPhoneFieldCode: Yup.string().when(
    ['$formValidations', '$isCreditCard'],
    (formValidations: PhoneValidations, isCreditCard: boolean, schema: Yup.StringSchema) =>
      isCreditCard
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                formValidations?.workPhone?.minFieldCodeLength || 0,
                'general_minCharacter_errorMessage'
              )
            )
        : schema.notRequired()
  ),
  workPhoneTelephoneNo: Yup.string().when(
    ['$formValidations', '$isCreditCard'],
    (formValidations: PhoneValidations, isCreditCard: boolean, schema: Yup.StringSchema) =>
      isCreditCard
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                formValidations?.workPhone?.minTelephoneNoLength || 0,
                'general_minCharacter_errorMessage'
              )
            )
        : schema.notRequired()
  ),
  contactPersonMobilePhone1Country: Yup.object()
    .nullable()
    .when('$isCreditCard', (isCreditCard: boolean, schema: Yup.StringSchema) =>
      isCreditCard ? schema.required('general_requiredField_errorMessage') : schema.notRequired()
    ),
  contactPersonMobilePhone1FieldCode: Yup.string().when(
    ['$formValidations', '$isCreditCard'],
    (formValidations: PhoneValidations, isCreditCard: boolean, schema: Yup.StringSchema) =>
      isCreditCard
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                formValidations?.contactPersonMobilePhone1?.minFieldCodeLength || 0,
                'general_minCharacter_errorMessage'
              )
            )
        : schema.notRequired()
  ),
  contactPersonMobilePhone1TelephoneNo: Yup.string().when(
    ['$formValidations', '$isCreditCard'],
    (formValidations: PhoneValidations, isCreditCard: boolean, schema: Yup.StringSchema) =>
      isCreditCard
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                formValidations?.contactPersonMobilePhone1?.minTelephoneNoLength || 0,
                'general_minCharacter_errorMessage'
              )
            )
        : schema.notRequired()
  ),
  contactPersonMobilePhone2Country: Yup.object().nullable(),
  contactPersonMobilePhone2FieldCode: Yup.string().when(
    ['$formValidations', '$isCreditCard'],
    (formValidations: PhoneValidations, isCreditCard: boolean, schema: Yup.StringSchema) =>
      isCreditCard
        ? schema.test(
            'fixed-length',
            'general_minCharacter_errorMessage',
            (val) =>
              val === '' ||
              val.length >= (formValidations?.contactPersonMobilePhone2?.minFieldCodeLength || 0)
          )
        : schema.notRequired()
  ),
  contactPersonMobilePhone2TelephoneNo: Yup.string().when(
    ['$formValidations', '$isCreditCard'],
    (formValidations: PhoneValidations, isCreditCard: boolean, schema: Yup.StringSchema) =>
      isCreditCard
        ? schema.test(
            'fixed-length',
            'general_minCharacter_errorMessage',
            (val) =>
              val === '' ||
              val.length >= (formValidations?.contactPersonMobilePhone2?.minTelephoneNoLength || 0)
          )
        : schema.notRequired()
  ),
});

export const CustomerDetailSchema = Yup.object().shape({
  faceControl: Yup.bool(),
  maritalStatus: Yup.object().nullable().required('general_requiredField_errorMessage'),
  numberOfChildren: Yup.string().when(
    '$isCreditCard',
    (isCreditCard: boolean, schema: Yup.StringSchema) =>
      isCreditCard ? schema.required('general_requiredField_errorMessage') : schema.notRequired()
  ),
  educationStatus: Yup.object()
    .nullable()
    .when('$isCreditCard', (isCreditCard: boolean, schema: Yup.StringSchema) =>
      isCreditCard ? schema.required('general_requiredField_errorMessage') : schema.notRequired()
    ),
  isGovernmentOfficer: Yup.bool(),
  hasRelativeForeignPublicOffical: Yup.object()
    .nullable()
    .required('general_requiredField_errorMessage'),
  hasUSAPermit: Yup.object().nullable().required('general_requiredField_errorMessage'),
  hasTravelPassport: Yup.object()
    .nullable()
    .when('$isCreditCard', (isCreditCard: boolean, schema: Yup.ObjectSchema) =>
      isCreditCard ? schema.required('general_requiredField_errorMessage') : schema.notRequired()
    ),
  hasCEBLoanOfRelatives: Yup.object()
    .nullable()
    .when('$isCreditCard', (isCreditCard: boolean, schema: Yup.ObjectSchema) =>
      isCreditCard ? schema.required('general_requiredField_errorMessage') : schema.notRequired()
    ),
});

export const Benefits3rdPartySchema = Yup.object().shape({
  hasBenefitsThirdParty: Yup.object().nullable().required('general_requiredField_errorMessage'),
  beneficiaryName: Yup.string().when(
    'hasBenefitsThirdParty',
    (hasBenefitsThirdParty: Option, schema: Yup.StringSchema) =>
      hasBenefitsThirdParty?.value === YES_NO.YES
        ? schema.required('general_requiredField_errorMessage')
        : schema.notRequired()
  ),
  birthPlace: Yup.string().when(
    'hasBenefitsThirdParty',
    (hasBenefitsThirdParty: Option, schema: Yup.StringSchema) =>
      hasBenefitsThirdParty?.value === YES_NO.YES
        ? schema.required('general_requiredField_errorMessage')
        : schema.notRequired()
  ),
  birthDate: Yup.date().when(
    ['hasBenefitsThirdParty', '$todayMaxControl'],
    (hasBenefitsThirdParty: Option, todayMaxControl: Date, schema: Yup.DateSchema) =>
      hasBenefitsThirdParty?.value === YES_NO.YES
        ? schema
            .nullable()
            .min(MINIMUM_DATE_CONTROL, 'cardEntry_minimumYear_errorMessage')
            .max(todayMaxControl, 'general_FutureDateCanNotSelected_errorMessage')
            .required('general_requiredField_errorMessage')
        : schema.nullable().notRequired()
  ),
  identityType: Yup.object().when(
    'hasBenefitsThirdParty',
    (hasBenefitsThirdParty: Option, schema: Yup.ObjectSchema) =>
      hasBenefitsThirdParty?.value === YES_NO.YES
        ? schema.nullable().required('general_requiredField_errorMessage')
        : schema.nullable().notRequired()
  ),
  identityDocumentSerialCode: Yup.string().when(
    '$formValidations',
    (formValidations: ThirdPartyPageValidationComponentsResponse[], schema: Yup.StringSchema) =>
      formValidations[0]?.series.required
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                formValidations[0]?.series.minimumLength || 0,
                'general_minCharacter_errorMessage'
              )
            )
        : schema
            .notRequired()
            .test(
              'fixed-length',
              'general_minCharacter_errorMessage',
              (val) => val === '' || val.length >= (formValidations[0]?.series.minimumLength || 0)
            )
  ),
  identityDocumentSerialNumber: Yup.string().when(
    '$formValidations',
    (formValidations: ThirdPartyPageValidationComponentsResponse[], schema: Yup.StringSchema) =>
      formValidations[0]?.documentNumber.required
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                formValidations[0]?.documentNumber.minimumLength || 0,
                'general_minCharacter_errorMessage'
              )
            )
        : schema
            .notRequired()
            .test(
              'fixed-length',
              'general_minCharacter_errorMessage',
              (val) =>
                val === '' || val.length >= (formValidations[0]?.documentNumber.minimumLength || 0)
            )
  ),
  issueDate: Yup.date().when(
    ['hasBenefitsThirdParty', '$todayMaxControl'],
    (hasBenefitsThirdParty: Option, todayMaxControl: Date, schema: Yup.DateSchema) =>
      hasBenefitsThirdParty?.value === YES_NO.YES
        ? schema
            .nullable()
            .min(MINIMUM_DATE_CONTROL, 'cardEntry_minimumYear_errorMessage')
            .max(todayMaxControl, 'general_FutureDateCanNotSelected_errorMessage')
            .required('general_requiredField_errorMessage')
        : schema.nullable().notRequired()
  ),
  issuedBy: Yup.string().when(
    '$formValidations',
    (formValidations: ThirdPartyPageValidationComponentsResponse[], schema: Yup.StringSchema) =>
      formValidations[0]?.issuedBy.required
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                formValidations[0]?.issuedBy.minimumLength || 0,
                'general_minCharacter_errorMessage'
              )
            )
        : schema
            .test(
              'fixed-length',
              'general_minCharacter_errorMessage',
              (val) => val === '' || val.length >= (formValidations[0]?.issuedBy.minimumLength || 0)
            )
            .notRequired()
  ),
  unitCode: Yup.string().when(
    '$formValidations',
    (formValidations: ThirdPartyPageValidationComponentsResponse[], schema: Yup.StringSchema) =>
      formValidations[0]?.unitCode.required
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                formValidations[0]?.unitCode.minimumLength || 0,
                'general_minCharacter_errorMessage'
              )
            )
        : schema
            .test(
              'fixed-length',
              'general_minCharacter_errorMessage',
              (val) => val === '' || val.length >= (formValidations[0]?.unitCode.minimumLength || 0)
            )
            .notRequired()
  ),
  inn: Yup.string().when(
    '$formValidations',
    (formValidations: ThirdPartyPageValidationComponentsResponse[], schema: Yup.StringSchema) =>
      formValidations[0]?.inn?.required
        ? schema.test(
            'len',
            'cardEntry_benefit3rdParty_innControl_errorMessage',
            (value) => value === '' || value.length === 12
          )
        : schema.notRequired()
  ),
  hasControlPerson: Yup.object().nullable().required('general_requiredField_errorMessage'),
  beneficiaryName2: Yup.string().when(
    'hasControlPerson',
    (hasControlPerson: Option, schema: Yup.StringSchema) =>
      hasControlPerson?.value === YES_NO.YES
        ? schema.required('general_requiredField_errorMessage')
        : schema.notRequired()
  ),
  birthPlace2: Yup.string().when(
    'hasControlPerson',
    (hasControlPerson: Option, schema: Yup.StringSchema) =>
      hasControlPerson?.value === YES_NO.YES
        ? schema.required('general_requiredField_errorMessage')
        : schema.notRequired()
  ),
  birthDate2: Yup.date().when(
    ['hasControlPerson', '$todayMaxControl'],
    (hasControlPerson: Option, todayMaxControl: Date, schema: Yup.DateSchema) =>
      hasControlPerson?.value === YES_NO.YES
        ? schema
            .nullable()
            .min(MINIMUM_DATE_CONTROL, 'cardEntry_minimumYear_errorMessage')
            .max(todayMaxControl, 'general_FutureDateCanNotSelected_errorMessage')
            .required('general_requiredField_errorMessage')
        : schema.nullable().notRequired()
  ),
  identityType2: Yup.object().when(
    'hasControlPerson',
    (hasControlPerson: Option, schema: Yup.ObjectSchema) =>
      hasControlPerson?.value === YES_NO.YES
        ? schema.nullable().required('general_requiredField_errorMessage')
        : schema.nullable().notRequired()
  ),
  identityDocumentSerialCode2: Yup.string().when(
    '$formValidations',
    (formValidations: ThirdPartyPageValidationComponentsResponse[], schema: Yup.StringSchema) =>
      formValidations[1]?.series.required
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                formValidations[1]?.series.minimumLength || 0,
                'general_minCharacter_errorMessage'
              )
            )
        : schema
            .notRequired()
            .test(
              'fixed-length',
              'general_minCharacter_errorMessage',
              (val) => val === '' || val.length >= (formValidations[1]?.series.minimumLength || 0)
            )
  ),
  identityDocumentSerialNumber2: Yup.string().when(
    '$formValidations',
    (formValidations: ThirdPartyPageValidationComponentsResponse[], schema: Yup.StringSchema) =>
      formValidations[1]?.documentNumber.required
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                formValidations[1]?.documentNumber.minimumLength || 0,
                'general_minCharacter_errorMessage'
              )
            )
        : schema
            .notRequired()
            .test(
              'fixed-length',
              'general_minCharacter_errorMessage',
              (val) =>
                val === '' || val.length >= (formValidations[1]?.documentNumber.minimumLength || 0)
            )
  ),
  issueDate2: Yup.date().when(
    ['hasControlPerson', '$todayMaxControl'],
    (hasControlPerson: Option, todayMaxControl: Date, schema: Yup.DateSchema) =>
      hasControlPerson?.value === YES_NO.YES
        ? schema
            .nullable()
            .min(MINIMUM_DATE_CONTROL, 'cardEntry_minimumYear_errorMessage')
            .max(todayMaxControl, 'general_FutureDateCanNotSelected_errorMessage')
            .required('general_requiredField_errorMessage')
        : schema.nullable().notRequired()
  ),
  issuedBy2: Yup.string().when(
    '$formValidations',
    (formValidations: ThirdPartyPageValidationComponentsResponse[], schema: Yup.StringSchema) =>
      formValidations[1]?.issuedBy.required
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                formValidations[1]?.issuedBy.minimumLength || 0,
                'general_minCharacter_errorMessage'
              )
            )
        : schema
            .test(
              'fixed-length',
              'general_minCharacter_errorMessage',
              (val) => val === '' || val.length >= (formValidations[1]?.issuedBy.minimumLength || 0)
            )
            .notRequired()
  ),
  unitCode2: Yup.string().when(
    '$formValidations',
    (formValidations: ThirdPartyPageValidationComponentsResponse[], schema: Yup.StringSchema) =>
      formValidations[1]?.unitCode.required
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                formValidations[1]?.unitCode.minimumLength || 0,
                'general_minCharacter_errorMessage'
              )
            )
        : schema
            .test(
              'fixed-length',
              'general_minCharacter_errorMessage',
              (val) => val === '' || val.length >= (formValidations[1]?.unitCode.minimumLength || 0)
            )
            .notRequired()
  ),
  inn2: Yup.string().when(
    '$formValidations',
    (formValidations: ThirdPartyPageValidationComponentsResponse[], schema: Yup.StringSchema) =>
      formValidations[1]?.inn?.required
        ? schema.test(
            'len',
            'cardEntry_benefit3rdParty_innControl_errorMessage',
            (value) => value === '' || value.length === 12
          )
        : schema.notRequired()
  ),
});

export const TaxNumberSchema = Yup.object().shape({
  taxNumber: Yup.string().test(
    'len',
    'cardEntry_taxInfo_innControl_errorMessage',
    (value) => value === '' || value.length === 12
  ),
});

export const TaxResidencySchema = Yup.object().shape({
  taxResidenceCountry: Yup.object().nullable().required('general_requiredField_errorMessage'),
  hasTaxNumberProvided: Yup.object().when(
    'taxResidenceCountry',
    (taxResidenceCountry: Option, schema: Yup.ObjectSchema) =>
      taxResidenceCountry?.value === 'RU'
        ? schema.nullable().notRequired()
        : schema.nullable().required('general_requiredField_errorMessage')
  ),
  differentumber: Yup.bool(),
  foreignTaxNumber: Yup.string().when(
    ['hasTaxNumberProvided', 'differentNumber', '$formValidations'],
    (
      hasTaxNumberProvided: Option,
      differentNumber: boolean,
      formValidations: CustomerTaxInfoFormatResponse,
      schema: Yup.ObjectSchema
    ) =>
      hasTaxNumberProvided?.value === IS_PROVIDED.PROVIDED
        ? schema
            .required('general_requiredField_errorMessage')
            .test(
              minimumLengthTest(
                (differentNumber
                  ? formValidations?.analogueInnMinLength
                  : formValidations.innMinLength) || 0,
                'general_minCharacter_errorMessage'
              )
            )
            .test(
              'max-length',
              'general_maxCharacter_errorMessage',
              (val) =>
                val.length <=
                ((differentNumber
                  ? formValidations?.analogueInnMaxLength
                  : formValidations.innMaxLength) || 0)
            )
        : schema.notRequired()
  ),
});

export const CardApplicationDetailSchema = Yup.object().shape({
  customerAccount: Yup.object()
    .nullable()
    .when(
      ['hasNoAccount', 'openNewAccount', '$isCreditCard', '$isCardPlus'],
      (
        hasNoAccount: Yup.BooleanSchema,
        openNewAccount: Yup.BooleanSchema,
        isCreditCard: boolean,
        isCardPlus: boolean,
        schema: Yup.ObjectSchema
      ) =>
        hasNoAccount || openNewAccount || isCreditCard || !isCardPlus
          ? schema.notRequired()
          : schema.required('general_requiredField_errorMessage')
    ),
  openNewAccount: Yup.bool(),
  cardEmbossedName: Yup.string().when(
    'isNoNameCard',
    (isNoNameCard: Yup.BooleanSchema, schema: Yup.StringSchema) =>
      isNoNameCard ? schema.notRequired() : schema.required('general_requiredField_errorMessage')
  ),
  isNoNameCard: Yup.bool(),
  cardNumber: Yup.string().when(
    'isNoNameCard',
    (isNoNameCard: Yup.BooleanSchema, schema: Yup.StringSchema) =>
      isNoNameCard ? schema.required('general_requiredField_errorMessage') : schema.notRequired()
  ),
  lastFourDigits: Yup.string().when(
    'isNoNameCard',
    (isNoNameCard: Yup.BooleanSchema, schema: Yup.StringSchema) =>
      isNoNameCard ? schema.required('general_requiredField_errorMessage') : schema.notRequired()
  ),
  promotionName: Yup.object().nullable(),
  promotionDaysNumber: Yup.string(),
  salaryCompanyCode: Yup.string(),
  deliveryChannelCode: Yup.string(),
  isMarketingViaEmail: Yup.bool(),
  hasNoAccount: Yup.bool(),
  branch: Yup.object().nullable().required('general_requiredField_errorMessage'),
  metroCardNumber: Yup.string().when(
    '$metroCardNumberLength',
    (metroCardNumberLength: number, schema: Yup.StringSchema) =>
      metroCardNumberLength > 0
        ? schema.required('general_requiredField_errorMessage')
        : schema.notRequired()
  ),
});

export const CardSendingInformationSchema = Yup.object().shape({
  cardDeliveryAddress: Yup.object().nullable().required('general_requiredField_errorMessage'),
  cardDeliveryBranch: Yup.object()
    .nullable()
    .when(
      'deliveryComboType',
      (deliveryComboType: CardDeliveryComboType, schema: Yup.StringSchema) =>
        deliveryComboType === CardDeliveryComboType.NONE
          ? schema.notRequired()
          : schema.required('general_requiredField_errorMessage')
    ),
  instantEmbossing: Yup.bool(),
  pinCodeDeliveryAddress: Yup.object()
    .nullable()
    .when('isBackOfficePos', (isBackOfficePos: boolean, schema: Yup.ObjectSchema) =>
      isBackOfficePos ? schema.required('general_requiredField_errorMessage') : schema.notRequired()
    ),
  pinCodeDeliveryBranch: Yup.object()
    .nullable()
    .when(
      ['pinDeliveryComboType', 'isBackOfficePos'],
      (
        pinDeliveryComboType: CardDeliveryComboType,
        isBackOfficePos: boolean,
        schema: Yup.ObjectSchema
      ) =>
        isBackOfficePos && pinDeliveryComboType !== CardDeliveryComboType.NONE
          ? schema.required('general_requiredField_errorMessage')
          : schema.notRequired()
    ),
  statementPeriod: Yup.object()
    .nullable()
    .when('$isCreditCard', (isCreditCard: boolean, schema: Yup.ObjectSchema) =>
      isCreditCard ? schema.required('general_requiredField_errorMessage') : schema.notRequired()
    ),
  sendViaEmail: Yup.bool(),
  smsAlert: Yup.bool(),
  lifeInsurance: Yup.bool(),
  consentScoring: Yup.bool(),
});
const INN_NOT_REQUIRED_OCCUPATION_CODES = [
  'PP', // Частная практика
  'ST', // Студент
  'SE', // Самозанятый
  'IE', // Собственный бизнес
  'CC', // Служащий
  'II', // Практикант
  'PE', // Пенсионер
  'HS', // Домохозяйка/Домохозяин
  'UN', // Безработный
];

const NOT_JOB_OCCUPATION_CODES = ['PE', 'HS', 'UN'];
export const isInnNotRequiredOccupation = (occ: Option | null): boolean =>
  !!occ && INN_NOT_REQUIRED_OCCUPATION_CODES.includes(occ.value);
export const isNotJobOccupation = (occ: Option | null): boolean =>
  !!occ && NOT_JOB_OCCUPATION_CODES.includes(occ.value);
const innChecksumIsValid = (inn: string): boolean => {
  const digits = inn.split('').map(Number);
  if (inn.length === 10) {
    // логика для 10-значного ИНН
    const weights = [2, 4, 10, 3, 5, 9, 4, 6, 8, 0];
    const sum = weights.reduce((acc, w, i) => acc + w * digits[i], 0);
    let control = sum % 11;
    if (control > 9) control %= 10;
    return control === digits[9];
  }
  if (inn.length === 12) {
    // Логика для 12-значного ИНН (юридические лица)
    // 1. Расчет первой контрольной цифры (11-я позиция)
    const weights1 = [7, 2, 4, 10, 3, 5, 9, 4, 6, 8];
    const sum1 = weights1.reduce((acc, w, i) => acc + w * digits[i], 0);
    const control1 = sum1 % 11;
    if (control1 !== digits[10]) return false;

    // 2. Расчет второй контрольной цифры (12-я позиция)
    const weights2 = [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8];
    const sum2 = weights2.reduce((acc, w, i) => acc + w * digits[i], 0);
    const control2 = sum2 % 11;
    return control2 === digits[11];
  }
  return false;
};
export const ScoringInformationSchema = Yup.object().shape({
  occupation: Yup.object().nullable().required('general_requiredField_errorMessage'),
  companyName: Yup.string().when('occupation', {
    is: (occ: Option | null) => !isNotJobOccupation(occ),
    then: Yup.string().required('general_requiredField_errorMessage'),
    otherwise: Yup.string().notRequired(),
  }),
  workInn: Yup.string()
    .test(
      'len',
      'cardEntry_workInn_length_errorMessage',
      (val) => val === '' || val?.length === 10 || val?.length === 12
    )
    .test('inn-checksum', 'cardEntry_workInn_checksum_errorMessage', (val) => {
      if (!val) return true;
      return innChecksumIsValid(val);
    })
    .when('occupation', {
      is: (occ: Option | null) => !isNotJobOccupation(occ) && !isInnNotRequiredOccupation(occ),
      then: Yup.string().required('general_requiredField_errorMessage'),
      otherwise: Yup.string().notRequired(),
    }),
  jobTitle: Yup.object()
    .nullable()
    .when('occupation', {
      is: (occ: Option | null) => !isNotJobOccupation(occ),
      then: Yup.object().required('general_requiredField_errorMessage'),
      otherwise: Yup.object().notRequired(),
    }),
  jobSector: Yup.object()
    .nullable()
    .when('occupation', {
      is: (occ: Option | null) => !isNotJobOccupation(occ),
      then: Yup.object().required('general_requiredField_errorMessage'),
      otherwise: Yup.object().notRequired(),
    }),
  businessLine: Yup.object()
    .nullable()
    .when('occupation', {
      is: (occ: Option | null) => !isNotJobOccupation(occ),
      then: Yup.object().required('general_requiredField_errorMessage'),
      otherwise: Yup.object().notRequired(),
    }),
  jobNumberForLast3Years: Yup.string().required('general_requiredField_errorMessage'),
  employeeNumber: Yup.object()
    .nullable()
    .when('occupation', {
      is: (occ: Option | null) => !isNotJobOccupation(occ),
      then: Yup.object().required('general_requiredField_errorMessage'),
      otherwise: Yup.object().notRequired(),
    }),
  is2NdflCertificateProvided: Yup.object()
    .nullable()
    .when('occupation', {
      is: (occ: Option | null) => !isNotJobOccupation(occ),
      then: Yup.object().required('general_requiredField_errorMessage'),
      otherwise: Yup.object().notRequired(),
    }),
  currentWorkBeginningMonth: Yup.object()
    .nullable()
    .when('occupation', {
      is: (occ: Option | null) => !isNotJobOccupation(occ),
      then: Yup.object().required('general_requiredField_errorMessage'),
      otherwise: Yup.object().notRequired(),
    }),
  currentWorkBeginningYear: Yup.object()
    .nullable()
    .when('occupation', {
      is: (occ: Option | null) => !isNotJobOccupation(occ),
      then: Yup.object().required('general_requiredField_errorMessage'),
      otherwise: Yup.object().notRequired(),
    }),
  workLifeBeginningMonth: Yup.object().nullable().required('general_requiredField_errorMessage'),
  workLifeBeginningYear: Yup.object().nullable().required('general_requiredField_errorMessage'),
  isGovernmentOrganisation: Yup.string().nullable().required('general_requiredField_errorMessage'),
  requestedLimit: Yup.string().when(
    '$limitDisplay',
    (limitDisplay: ScoringJobPageVisibility, schema: Yup.NumberSchema) =>
      limitDisplay?.isRequestedLimitVisible
        ? schema
            .test('min-amount', 'cardEntry_jobScoring_moreThanZero_errorMessage', (val) =>
              limitDisplay?.canRequestedLimitBeZero
                ? Number(formatBlank(val)) >= 0
                : Number(formatBlank(val)) > 0
            )
            .required('general_requiredField_errorMessage')
        : schema.notRequired()
  ),
  cardLimitPercentage: Yup.number().when(
    '$limitDisplay',
    (limitDisplay: ScoringJobPageVisibility, schema: Yup.NumberSchema) =>
      limitDisplay?.isCardLimitPercentageVisible
        ? schema
            .test(
              'min-amount',
              'cardEntry_jobScoring_moreThanZero_errorMessage',
              (val) => Number(val) > 0
            )
            .required('general_requiredField_errorMessage')
        : schema.notRequired()
  ),
  income: Yup.string()
    .test(
      'min-amount',
      'cardEntry_jobScoring_moreThanZero_errorMessage',
      (val) => Number(formatBlank(val)) > 0
    )
    .required('general_requiredField_errorMessage'),
  otherIncome: Yup.string(),
});

export const ApplicationDocumentSchema = Yup.object().shape({
  files: Yup.mixed()
    .required('cardEntry_appDocumentUpload_noUploadedDocument_errorMessage')
    .test(
      'file-size',
      'general_uploadFileSize_errorMessage',
      (files) => files && files[0] && files[0].size < MAX_FILE_UPLOAD_LIMIT_IN_BYTES
    )
    .test(
      'file-format',
      'general_uploadFileFormat_errorMessage',
      (files) => files && files[0] && ACCEPTED_FILE_UPLOAD_FORMATS_ARRAY.includes(files[0].type)
    ),
  filePath: Yup.string(),
});

export const NewSettlementSchema = Yup.object().shape({
  isComboVisible: Yup.boolean().required(),
  beginDate: Yup.date().required('general_requiredField_errorMessage').nullable(),
  endDate: Yup.date().when('beginDate', {
    is: (date) => date !== null && date !== undefined,
    then: Yup.date()
      .nullable()
      .min(Yup.ref('beginDate'), 'general_endDateControl_errorMessage')
      .required('general_requiredField_errorMessage'),
    otherwise: Yup.date().nullable().required('general_requiredField_errorMessage'),
  }),
  loanType: Yup.object().nullable().required('general_requiredField_errorMessage'),
  settlementType: Yup.object().nullable().required('general_requiredField_errorMessage'),
  organization: Yup.object().nullable().required('general_requiredField_errorMessage'),
  insuranceCompany: Yup.object().when('isComboVisible', {
    is: (val) => val === true,
    then: Yup.object().nullable().required('general_requiredField_errorMessage'),
    otherwise: Yup.object().nullable().notRequired(),
  }),
});

export const NewObserveSchema = Yup.object().shape({
  beginDate: Yup.date().required('general_requiredField_errorMessage').nullable(),
  endDate: Yup.date().when(
    ['beginDate', 'loanType'],
    (beginDate: Date, loanType: Option, schema: Yup.DateSchema) =>
      beginDate
        ? schema
            .nullable()
            .min(beginDate, 'general_endDateControl_errorMessage')
            .max(
              loanType.value === '300550' ? addDays(beginDate, 7) : addDays(beginDate, 15),
              loanType.value === '300550'
                ? 'newObserve_maxDateIntervalForCarLoan_errorMessage'
                : 'newObserve_maxDateInterval_errorMessage'
            )
            .required('general_requiredField_errorMessage')
        : schema.nullable().required('general_requiredField_errorMessage')
  ),
  loanType: Yup.object().nullable().required('general_requiredField_errorMessage'),
  shopType: Yup.object().nullable(),
  surname: Yup.string(),
});
export const UploadDocumentCreditSchema = Yup.object().shape({
  beginDate: Yup.date().required('general_requiredField_errorMessage').nullable(),
  endDate: Yup.date().when('beginDate', (beginDate: Date, schema: Yup.DateSchema) =>
    beginDate
      ? schema
          .nullable()
          .min(beginDate, 'general_endDateControl_errorMessage')
          .max(addDays(beginDate, 15), 'newObserve_maxDateInterval_errorMessage')
          .required('general_requiredField_errorMessage')
      : schema.nullable().required('general_requiredField_errorMessage')
  ),
  creditStats: Yup.object().nullable(),
  documentType: Yup.object().nullable().required('general_requiredField_errorMessage'),
});

export const TransferHistorySchema = Yup.object().shape({
  beginDate: Yup.date().when('$todayMaxControl', (todayMaxControl: Date, schema: Yup.DateSchema) =>
    schema
      .nullable()
      .min(MINIMUM_DATE_CONTROL, 'cardEntry_minimumYear_errorMessage')
      .max(todayMaxControl, 'general_FutureDateCanNotSelected_errorMessage')
      .required('general_requiredField_errorMessage')
  ),
  endDate: Yup.date().when(
    ['beginDate', '$todayMaxControl'],
    (beginDate: Date, todayMaxControl: Date, schema: Yup.DateSchema) =>
      beginDate
        ? schema
            .nullable()
            .min(beginDate, 'general_endDateControl_errorMessage')
            .max(
              addDays(beginDate, 30),
              'callCenter_internetBanking_interbankMoneyTransfersHistory_minDateInterval_errorMessage'
            )
            .required('general_requiredField_errorMessage')
        : schema
            .nullable()
            .min(MINIMUM_DATE_CONTROL, 'cardEntry_minimumYear_errorMessage')
            .max(todayMaxControl, 'general_FutureDateCanNotSelected_errorMessage')
            .required('general_requiredField_errorMessage')
  ),
});
export const IntrabankTransferHistorySchema = Yup.object().shape({
  beginDate: Yup.date().when('$todayMaxControl', (todayMaxControl: Date, schema: Yup.DateSchema) =>
    schema
      .nullable()
      .min(MINIMUM_DATE_CONTROL, 'cardEntry_minimumYear_errorMessage')
      .max(todayMaxControl, 'general_FutureDateCanNotSelected_errorMessage')
      .required('general_requiredField_errorMessage')
  ),
  endDate: Yup.date().when(
    ['beginDate', '$todayMaxControl'],
    (beginDate: Date, todayMaxControl: Date, schema: Yup.DateSchema) =>
      beginDate
        ? schema
            .nullable()
            .min(beginDate, 'general_endDateControl_errorMessage')
            .max(
              addDays(beginDate, 30),
              'callCenter_internetBanking_interbankMoneyTransfersHistory_minDateInterval_errorMessage'
            )
            .required('general_requiredField_errorMessage')
        : schema
            .nullable()
            .min(MINIMUM_DATE_CONTROL, 'cardEntry_minimumYear_errorMessage')
            .max(todayMaxControl, 'general_FutureDateCanNotSelected_errorMessage')
            .required('general_requiredField_errorMessage')
  ),
  cardType: Yup.object().nullable().required('general_requiredField_errorMessage'),
});

export const PlasticCardRegistrationSchema = Yup.object().shape({
  pointOfSale: Yup.object().required('general_requiredField_errorMessage').nullable(),
  operations: Yup.object().required('general_requiredField_errorMessage').nullable(),
  accountType: Yup.object().required('general_requiredField_errorMessage').nullable(),
  quantity: Yup.string().required('general_requiredField_errorMessage'),
  actDocumentCreateDate: Yup.date().when(
    '$todayMaxControl',
    (todayMaxControl: Date, schema: Yup.DateSchema) =>
      schema
        .nullable()
        .min(MINIMUM_DATE_CONTROL, 'cardEntry_minimumYear_errorMessage')
        .max(todayMaxControl, 'general_FutureDateCanNotSelected_errorMessage')
        .required('general_requiredField_errorMessage')
  ),
});

export const OperationsPerformedSchema = Yup.object().shape({
  pointOfSale: Yup.object().required('general_requiredField_errorMessage').nullable(),
  beginDate: Yup.date().when('$todayMaxControl', (todayMaxControl: Date, schema: Yup.DateSchema) =>
    schema
      .nullable()
      .min(MINIMUM_DATE_CONTROL, 'cardEntry_minimumYear_errorMessage')
      .max(todayMaxControl, 'general_startDateControl_errorMessage')
  ),
  endDate: Yup.date().when(
    ['beginDate', '$todayMaxControl'],
    (beginDate: Date, todayMaxControl: Date, schema: Yup.DateSchema) =>
      beginDate
        ? schema
            .nullable()
            .min(beginDate, 'general_endDateControl_errorMessage')
            .max(
              addDays(beginDate, 95),
              'plasticCardRegistration_accountOperations_maxDateInterval_errorMessage'
            )
        : schema
            .nullable()
            .min(MINIMUM_DATE_CONTROL, 'cardEntry_minimumYear_errorMessage')
            .max(todayMaxControl, 'general_FutureDateCanNotSelected_errorMessage')
  ),
});

export const AccountsSchema = Yup.object().shape({
  pointOfSale: Yup.object().required('general_requiredField_errorMessage').nullable(),
});

export const PeriodSchema = Yup.object().shape({
  beginDate: Yup.date().when('$todayMaxControl', (todayMaxControl: Date, schema: Yup.DateSchema) =>
    schema
      .nullable()
      .min(MINIMUM_DATE_CONTROL, 'cardEntry_minimumYear_errorMessage')
      .max(todayMaxControl, 'general_startDateControl_errorMessage')
      .required('general_requiredField_errorMessage')
  ),
  endDate: Yup.date().when(
    ['beginDate', '$todayMaxControl'],
    (beginDate: Date, todayMaxControl: Date, schema: Yup.DateSchema) =>
      beginDate
        ? schema
            .nullable()
            .min(beginDate, 'general_endDateControl_errorMessage')
            .max(
              addDays(beginDate, 95),
              'plasticCardRegistration_accountOperations_maxDateInterval_errorMessage'
            )
            .required('general_requiredField_errorMessage')
        : schema
            .nullable()
            .min(MINIMUM_DATE_CONTROL, 'cardEntry_minimumYear_errorMessage')
            .max(todayMaxControl, 'general_startDateControl_errorMessage')
            .required('general_requiredField_errorMessage')
  ),
});
export const PTSDocumentUploadSchema = Yup.object().shape({
  files: Yup.mixed().when('documentId', (documentId: Yup.StringSchema, schema: Yup.MixedSchema) =>
    !documentId
      ? schema
          .required('general_requiredField_errorMessage')
          .test(
            'file-size',
            'general_uploadFileSize_errorMessage',
            (files) => files && files[0] && files[0].size < MAX_FILE_UPLOAD_LIMIT_IN_BYTES
          )
          .test(
            'file-format',
            'general_uploadFileFormat_errorMessage',
            (files) =>
              files && files[0] && ACCEPTED_FILE_UPLOAD_FORMATS_ARRAY.includes(files[0].type)
          )
      : schema.notRequired()
  ),
  filePath: Yup.string(),
  explanation: Yup.string(),
  documentId: Yup.string(),
});
export const PTSNewRecordSchema = Yup.object().shape({
  levelZero: Yup.object().required('general_requiredField_errorMessage').nullable(),
  levelOne: Yup.object().required('general_requiredField_errorMessage').nullable(),
  levelTwo: Yup.object().when(
    'levelTwoRequired',
    (levelTwoRequired: boolean, schema: Yup.ObjectSchema) =>
      levelTwoRequired
        ? schema.required('general_requiredField_errorMessage').nullable()
        : schema.notRequired().nullable()
  ),
  levelThree: Yup.object().when(
    'levelThreeRequired',
    (levelThreeRequired: boolean, schema: Yup.ObjectSchema) =>
      levelThreeRequired
        ? schema.required('general_requiredField_errorMessage').nullable()
        : schema.notRequired().nullable()
  ),
  productType: Yup.object().when('levelOne', (levelOne: Option, schema: Yup.ObjectSchema) =>
    levelOne.value !== '13'
      ? schema.required('general_requiredField_errorMessage').nullable()
      : schema.notRequired().nullable()
  ),
  deadline: Yup.date().required('general_requiredField_errorMessage'),
  recordDetail: Yup.string().required('general_requiredField_errorMessage'),
});
export const PTSViewRecordsSchema = Yup.object().shape({
  startDate: Yup.date().when('endDate', (endDate: Date, schema: Yup.DateSchema) =>
    schema
      .nullable()
      .min(
        extractDays(endDate, 183),
        'problemTrackingSystem_recordSearch_maxDateInterval_errorMessage'
      )
      .max(endDate, 'general_startDateControl_errorMessage')
      .required('general_requiredField_errorMessage')
  ),
  endDate: Yup.date()
    .nullable()
    .min(Yup.ref('startDate'), 'general_endDateControl_errorMessage')
    // TODO: (hilmi.akdas) Convert to todayMaxControl - Cannot validate -> new Date()
    .max(
      new Date(new Date().setHours(24, 0, 0, 0) - 1),
      'general_FutureDateCanNotSelected_errorMessage'
    )
    .required('general_requiredField_errorMessage'),
});
export const PTSViewRecordsResultSchema = Yup.object().shape({
  result: Yup.string().required('general_requiredField_errorMessage'),
});

export const PPSimulationSchema = Yup.object().shape({
  loanType: Yup.object().nullable().required('general_requiredField_errorMessage'),
  retailOutlet: Yup.object().nullable().required('general_requiredField_errorMessage'),
  actionType: Yup.object().nullable().required('general_requiredField_errorMessage'),
  currencyType: Yup.object().nullable().required('general_requiredField_errorMessage'),
  creditPeriod: Yup.object().nullable().required('general_requiredField_errorMessage'),
  goodsTotal: Yup.string().required('general_requiredField_errorMessage'),
  lifeInsurance: Yup.object().nullable().required('general_requiredField_errorMessage'),
  kasko: Yup.string().when(
    '$isCarLoanSelected',
    (isCarLoanSelected: boolean, schema: Yup.StringSchema) =>
      isCarLoanSelected ? schema.required('general_requiredField_errorMessage') : schema.optional()
  ),
  kaskoPeriod: Yup.object()
    .nullable()
    .when('$isCarLoanSelected', (isCarLoanSelected: boolean, schema: Yup.StringSchema) =>
      isCarLoanSelected ? schema.required('general_requiredField_errorMessage') : schema.optional()
    ),
  osago: Yup.string().when(
    '$isCarLoanSelected',
    (isCarLoanSelected: boolean, schema: Yup.StringSchema) =>
      isCarLoanSelected ? schema.required('general_requiredField_errorMessage') : schema.optional()
  ),
  osagoPeriod: Yup.object()
    .nullable()
    .when('$isCarLoanSelected', (isCarLoanSelected: boolean, schema: Yup.StringSchema) =>
      isCarLoanSelected ? schema.required('general_requiredField_errorMessage') : schema.optional()
    ),
  policyCost: Yup.object()
    .nullable()
    .when('$isCarLoanSelected', (isCarLoanSelected: boolean, schema: Yup.StringSchema) =>
      isCarLoanSelected ? schema.required('general_requiredField_errorMessage') : schema.optional()
    ),
});
